import React, { useState, useEffect, useMemo } from 'react';
import { getAnalyticsData } from '../../utils/visitorTracking';
import Pagination from '../Pagination/Pagination';
import './PageVisitHeatmap.scss';

const PageVisitHeatmap = () => {
  const [visitorData, setVisitorData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  useEffect(() => {
    loadData();
    // Refresh data every 5 seconds
    const interval = setInterval(loadData, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const analyticsData = await getAnalyticsData();
      const data = analyticsData.allVisits || [];
      setVisitorData(data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading heatmap data:', error);
      setLoading(false);
    }
  };

  // Define all valid routes in the project
  const validRoutes = [
    '/',
    '/home',
    '/about',
    '/summit-objective',
    '/global-tour',
    '/sponsorship',
    '/services',
    '/project',
    '/testimonials',
    '/gallery',
    '/contact',
    '/404'
  ];

  // Check if route is a valid existing route in the project
  const isValidRoute = (route) => {
    // Normalize route (remove trailing slash except for root)
    const normalizedRoute = route === '/' ? '/' : route.replace(/\/$/, '');
    
    // Check exact matches
    if (validRoutes.includes(normalizedRoute)) {
      return true;
    }
    
    // Check dynamic routes: /service-single/:slug and /project-single/:slug
    if (normalizedRoute.startsWith('/service-single/') || 
        normalizedRoute.startsWith('/project-single/')) {
      return true;
    }
    
    return false;
  };

  // Check if route should be excluded from heatmap
  const shouldExcludeRoute = (route) => {
    // Exclude /admin routes
    if (route.startsWith('/admin')) {
      return true;
    }
    // Exclude analytics routes
    if (route === '/analytics' || route.startsWith('/analytics/')) {
      return true;
    }
    // Exclude login, forgot, register, privacy, and terms-and-conditions routes
    if (route === '/login' || route === '/forgot' || route === '/register' || 
        route === '/privacy' || route === '/terms-and-conditions') {
      return true;
    }
    // Only show valid routes (existing routes in the project)
    if (!isValidRoute(route)) {
      return true;
    }
    return false;
  };

  // Process data to create heatmap matrix
  const heatmapData = useMemo(() => {
    const data = visitorData;
    
    // Filter out excluded routes
    const filteredData = data.filter(visit => !shouldExcludeRoute(visit.route));
    
    // Get all unique routes (after filtering)
    const routes = [...new Set(filteredData.map(visit => visit.route))].sort();
    
    // Get unique IPs from filtered data
    const ipSet = new Set(filteredData.map(visit => visit.ip));
    const ips = Array.from(ipSet);
    
    // Create matrix: IP -> Route -> Count
    const matrix = {};
    ips.forEach(ip => {
      matrix[ip] = {};
      routes.forEach(route => {
        matrix[ip][route] = 0;
      });
    });
    
    // Count visits (only from filtered data)
    filteredData.forEach(visit => {
      if (matrix[visit.ip] && matrix[visit.ip][visit.route] !== undefined) {
        matrix[visit.ip][visit.route]++;
      }
    });
    
    return { matrix, routes, ips };
  }, [visitorData.length]);

  // Calculate pagination for IPs
  const totalPages = Math.ceil(heatmapData.ips.length / itemsPerPage);
  
  // Ensure current page is valid when data changes
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);
  
  const ipStartIndex = (currentPage - 1) * itemsPerPage;
  const ipEndIndex = ipStartIndex + itemsPerPage;
  const paginatedIPs = heatmapData.ips.slice(ipStartIndex, ipEndIndex);

  // Get color intensity based on visit count
  const getColorIntensity = (count, maxCount) => {
    if (maxCount === 0) return 0;
    const intensity = (count / maxCount) * 100;
    return Math.min(100, Math.max(0, intensity));
  };

  // Find max count for normalization
  const maxCount = useMemo(() => {
    let max = 0;
    Object.values(heatmapData.matrix).forEach(ipData => {
      Object.values(ipData).forEach(count => {
        if (count > max) max = count;
      });
    });
    return max;
  }, [heatmapData]);

  if (loading) {
    return (
      <div className="heatmap-loading">
        <p>Loading heatmap data...</p>
      </div>
    );
  }

  if (heatmapData.ips.length === 0 || heatmapData.routes.length === 0) {
    return (
      <div className="heatmap-empty">
        <p>No visitor data available to display heatmap.</p>
      </div>
    );
  }

  return (
    <div className="page-visit-heatmap">
      <div className="heatmap-header">
        <h3>
          <i className="ti-bar-chart-alt"></i> Page Visits Heatmap by IP Address
        </h3>
        <div className="heatmap-info">
          <span className="info-item">
            <span className="info-label">IPs:</span>
            <span className="info-value">{heatmapData.ips.length}</span>
          </span>
          <span className="info-item">
            <span className="info-label">Pages:</span>
            <span className="info-value">{heatmapData.routes.length}</span>
          </span>
        </div>
      </div>

      <div className="heatmap-legend">
        <div className="legend-label">Visit Intensity:</div>
        <div className="legend-scale">
          <span className="legend-item" style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)' }}>0</span>
          <span className="legend-item" style={{ backgroundColor: 'rgba(220, 38, 38, 0.3)' }}></span>
          <span className="legend-item" style={{ backgroundColor: 'rgba(220, 38, 38, 0.5)' }}></span>
          <span className="legend-item" style={{ backgroundColor: 'rgba(220, 38, 38, 0.7)' }}></span>
          <span className="legend-item" style={{ backgroundColor: 'rgba(220, 38, 38, 0.9)' }}></span>
          <span className="legend-item" style={{ backgroundColor: 'rgba(220, 38, 38, 1)' }}>{maxCount}+</span>
        </div>
      </div>

      <div className="heatmap-container">
        <div className="heatmap-scroll">
          <table className="heatmap-table">
            <thead>
              <tr>
                <th className="heatmap-corner">IP Address</th>
                {heatmapData.routes.map((route, index) => (
                  <th key={index} className="heatmap-route-header" title={route}>
                    {route.length > 20 ? route.substring(0, 20) + '...' : route}
                  </th>
                ))}
                <th className="heatmap-total-header">Total</th>
              </tr>
            </thead>
            <tbody>
              {paginatedIPs.map((ip, ipIndex) => {
                const ipData = heatmapData.matrix[ip];
                const totalVisits = Object.values(ipData).reduce((sum, count) => sum + count, 0);
                
                return (
                  <tr key={ipIndex}>
                    <td className="heatmap-ip-cell" title={ip}>
                      {ip.length > 15 ? ip.substring(0, 15) + '...' : ip}
                    </td>
                    {heatmapData.routes.map((route, routeIndex) => {
                      const count = ipData[route] || 0;
                      const intensity = getColorIntensity(count, maxCount);
                      const opacity = intensity / 100;
                      
                      return (
                        <td
                          key={routeIndex}
                          className="heatmap-cell"
                          style={{
                            backgroundColor: `rgba(220, 38, 38, ${Math.max(0.1, opacity)})`,
                            color: opacity > 0.5 ? '#fff' : '#333',
                            fontWeight: count > 0 ? '600' : '400'
                          }}
                          title={`${ip} → ${route}: ${count} visit(s)`}
                        >
                          {count > 0 ? count : ''}
                        </td>
                      );
                    })}
                    <td className="heatmap-total-cell">{totalVisits}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td className="heatmap-total-row">Total</td>
                {heatmapData.routes.map((route, index) => {
                  const routeTotal = heatmapData.ips.reduce((sum, ip) => {
                    return sum + (heatmapData.matrix[ip][route] || 0);
                  }, 0);
                  return (
                    <td key={index} className="heatmap-total-cell">
                      {routeTotal}
                    </td>
                  );
                })}
                <td className="heatmap-total-cell-final">
                  {heatmapData.ips.reduce((sum, ip) => {
                    return sum + Object.values(heatmapData.matrix[ip]).reduce((s, c) => s + c, 0);
                  }, 0)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="heatmap-pagination">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            totalItems={heatmapData.ips.length}
          />
        </div>
      )}
    </div>
  );
};

export default PageVisitHeatmap;

